import { useState, useMemo } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ActionButton } from "@/components/ui/action-button";
import { useVenues } from "@/hooks/useVenues";
import { useUserVenues, useSetUserVenues } from "@/hooks/useUsers";
import type { Venue } from "@/types";

interface Props {
  userId: string;
  userEmail: string;
}

function VenueList({
  userId,
  initialIds,
  venues,
  onClose,
}: {
  userId: string;
  initialIds: string[];
  venues: Venue[];
  onClose: () => void;
}) {
  const setVenues = useSetUserVenues();
  const [selected, setSelected] = useState(() => new Set(initialIds));

  const toggle = (venueId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(venueId)) next.delete(venueId);
      else next.add(venueId);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      await setVenues.mutateAsync({ userId, venueIds: [...selected] });
      toast.success("Lokalizacje zaktualizowane");
      onClose();
    } catch (err) {
      toast.error("Nie udało się zapisać lokalizacji", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const hasChanges =
    selected.size !== initialIds.length || [...selected].some((id) => !initialIds.includes(id));

  return (
    <>
      {venues.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">Brak dostępnych lokalizacji.</p>
      ) : (
        <div className="max-h-80 overflow-y-auto space-y-1 py-2">
          {venues.map((venue) => (
            <button
              key={venue.id}
              type="button"
              onClick={() => toggle(venue.id)}
              className="flex w-full items-center gap-3 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-left"
            >
              <Checkbox
                checked={selected.has(venue.id)}
                onCheckedChange={() => toggle(venue.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-sm">{venue.name}</span>
            </button>
          ))}
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Anuluj
        </Button>
        <Button onClick={handleSave} disabled={!hasChanges || setVenues.isPending}>
          {setVenues.isPending ? "Zapisywanie..." : "Zapisz"}
        </Button>
      </DialogFooter>
    </>
  );
}

export function UserVenuesDialog({ userId, userEmail }: Props) {
  const [open, setOpen] = useState(false);
  const { data: allVenues = [] } = useVenues();
  const { data: assignedIds = [], isLoading } = useUserVenues(userId, open);

  const visibleVenues = useMemo(() => allVenues.filter((v) => !v.isHidden), [allVenues]);

  return (
    <>
      <ActionButton tooltip="Lokalizacje" onClick={() => setOpen(true)}>
        <MapPin className="h-4 w-4" />
      </ActionButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Lokalizacje — {userEmail}</DialogTitle>
          </DialogHeader>
          {isLoading ? (
            <div className="space-y-2 py-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-8 rounded bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <VenueList
              key={assignedIds.join(",")}
              userId={userId}
              initialIds={assignedIds}
              venues={visibleVenues}
              onClose={() => setOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
